import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Briefcase, Inbox, FileText, Clock, Calendar, HardHat, Building2, 
  FolderOpen, StickyNote, Loader2, ChevronRight, Users, Bell,
  MessageSquare, ClipboardList, AlertTriangle, CheckCircle, ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    jobs: { newCount: 0, items: [] },
    communication: { unreadCount: 0, items: [] },
    reports: { newCount: 0, items: [] },
    assignments: { newCount: 0, upcomingCount: 0, items: [] },
    calendar: { todayCount: 0, upcomingCount: 0, items: [] },
    subcontractors: { newCount: 0, items: [] },
    branchInfo: { newCount: 0, items: [] },
    files: { recentCount: 0, items: [] },
    wall: { newCount: 0, items: [] }
  });

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Parallel fetch all data
      const [
        jobsRes,
        messagesRes,
        reportsRes,
        assignmentsRes,
        calendarRes,
        subcontractorsRes,
        wallRes,
        forwardedJobsRes
      ] = await Promise.all([
        // İşler - görülmemiş sayısı ve son işler
        axios.get(`${API}/work/jobs`, { headers }).catch(() => ({ data: [] })),
        // İletişim - okunmamış mesajlar
        axios.get(`${API}/work/messages/inbox`, { headers }).catch(() => ({ data: [] })),
        // Raporlar
        axios.get(`${API}/work/report-requests/my`, { headers }).catch(() => ({ data: { sent: [], received: [] } })),
        // İş Programı
        axios.get(`${API}/work/schedules`, { headers }).catch(() => ({ data: [] })),
        // Takvim - bu ay etkinlikler
        axios.get(`${API}/work/calendar/events`, { headers }).catch(() => ({ data: [] })),
        // Taşeronlar - yeni sayısı
        axios.get(`${API}/work/subcontractors/new-counts`, { headers }).catch(() => ({ data: { total_new: 0 } })),
        // Şube Duvarı - son paylaşımlar
        axios.get(`${API}/work/wall/posts`, { headers }).catch(() => ({ data: [] })),
        // İletilen İşler
        axios.get(`${API}/work/jobs/forwarded`, { headers }).catch(() => ({ data: [] }))
      ]);

      // Process Jobs - hem normal hem iletilen işleri dahil et
      const jobs = jobsRes.data || [];
      const forwardedJobs = forwardedJobsRes.data || [];
      const unseenJobs = jobs.filter(j => !j.is_seen);
      const unseenForwardedJobs = forwardedJobs.filter(j => !j.is_seen);
      const totalUnseenCount = unseenJobs.length + unseenForwardedJobs.length;
      const recentJobs = jobs.slice(0, 3);

      // Process Messages
      const messages = messagesRes.data || [];
      const unreadMessages = messages.filter(m => !m.is_read);

      // Process Reports
      const reportsData = reportsRes.data || { sent: [], received: [] };
      const allReports = [...(reportsData.received || [])];
      const unreadReports = allReports.filter(r => !r.is_read);

      // Process Assignments (İş Programı)
      const assignments = assignmentsRes.data || [];
      const unreadAssignments = assignments.filter(a => !a.read_by?.includes(user?.id) && a.owner_id !== user?.id);
      const upcomingAssignments = assignments.filter(a => {
        if (!a.start_date) return false;
        const startDate = new Date(a.start_date);
        const today = new Date();
        const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        return startDate >= today && startDate <= nextWeek;
      });

      // Process Calendar
      const events = calendarRes.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayEnd = new Date(today);
      todayEnd.setHours(23, 59, 59, 999);
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const todayEvents = events.filter(e => {
        const eventDate = new Date(e.start_date || e.date);
        return eventDate >= today && eventDate <= todayEnd;
      });
      const upcomingEvents = events.filter(e => {
        const eventDate = new Date(e.start_date || e.date);
        return eventDate > todayEnd && eventDate <= nextWeek;
      });

      // Process Subcontractors
      const subcontractorNewCount = subcontractorsRes.data?.total_new || 0;

      // Process Wall Posts
      const wallPosts = wallRes.data || [];
      const recentWallPosts = wallPosts.slice(0, 3);
      const unreadWallPosts = wallPosts.filter(p => !p.read_by?.includes(user?.id));

      setDashboardData({
        jobs: { 
          newCount: totalUnseenCount, 
          items: recentJobs.map(j => ({ 
            id: j.id, 
            title: j.name || j.konu, 
            status: j.status,
            date: j.created_at 
          }))
        },
        communication: { 
          unreadCount: unreadMessages.length, 
          items: unreadMessages.slice(0, 3).map(m => ({
            id: m.id,
            title: m.subject || 'Yeni Mesaj',
            sender: m.sender_name,
            date: m.created_at
          }))
        },
        reports: { 
          newCount: unreadReports.length, 
          items: unreadReports.slice(0, 3).map(r => ({
            id: r.id,
            title: r.title || r.report_type,
            sender: r.sender_name,
            date: r.created_at
          }))
        },
        assignments: { 
          newCount: unreadAssignments.length, 
          upcomingCount: upcomingAssignments.length,
          items: upcomingAssignments.slice(0, 3).map(a => ({
            id: a.id,
            title: a.title,
            date: a.start_date
          }))
        },
        calendar: { 
          todayCount: todayEvents.length, 
          upcomingCount: upcomingEvents.length,
          items: [...todayEvents, ...upcomingEvents].slice(0, 3).map(e => ({
            id: e.id,
            title: e.title,
            date: e.start_date || e.date
          }))
        },
        subcontractors: { 
          newCount: subcontractorNewCount, 
          items: [] 
        },
        branchInfo: { newCount: 0, items: [] },
        files: { recentCount: 0, items: [] },
        wall: { 
          newCount: unreadWallPosts.length, 
          items: recentWallPosts.map(p => ({
            id: p.id,
            title: p.content?.substring(0, 50) || 'Yeni Paylaşım',
            author: p.author_name,
            date: p.created_at
          }))
        }
      });

    } catch (error) {
      console.error('Dashboard verileri yüklenirken hata:', error);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    loadDashboardData();
    // Her 30 saniyede bir güncelle
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  // Özet kartları - sadece veri olanları göster
  const summaryCards = [
    {
      key: 'jobs',
      title: 'İŞLER',
      icon: Briefcase,
      path: '/work/jobs',
      color: 'blue',
      newCount: dashboardData.jobs.newCount,
      items: dashboardData.jobs.items,
      showIf: dashboardData.jobs.newCount > 0 || dashboardData.jobs.items.length > 0
    },
    {
      key: 'communication',
      title: 'İLETİŞİM',
      icon: Inbox,
      path: '/work/communication',
      color: 'indigo',
      newCount: dashboardData.communication.unreadCount,
      items: dashboardData.communication.items,
      showIf: dashboardData.communication.unreadCount > 0
    },
    {
      key: 'reports',
      title: 'RAPORLAR',
      icon: FileText,
      path: '/work/reports',
      color: 'green',
      newCount: dashboardData.reports.newCount,
      items: dashboardData.reports.items,
      showIf: dashboardData.reports.newCount > 0
    },
    {
      key: 'assignments',
      title: 'İŞ PROGRAMI',
      icon: Clock,
      path: '/work/assignments',
      color: 'purple',
      newCount: dashboardData.assignments.newCount,
      upcomingCount: dashboardData.assignments.upcomingCount,
      items: dashboardData.assignments.items,
      showIf: dashboardData.assignments.newCount > 0 || dashboardData.assignments.upcomingCount > 0
    },
    {
      key: 'calendar',
      title: 'TAKVİM',
      icon: Calendar,
      path: '/work/calendar',
      color: 'orange',
      todayCount: dashboardData.calendar.todayCount,
      upcomingCount: dashboardData.calendar.upcomingCount,
      items: dashboardData.calendar.items,
      showIf: dashboardData.calendar.todayCount > 0 || dashboardData.calendar.upcomingCount > 0
    },
    {
      key: 'subcontractors',
      title: 'TAŞERONLAR',
      icon: HardHat,
      path: '/work/subcontractors',
      color: 'amber',
      newCount: dashboardData.subcontractors.newCount,
      items: [],
      showIf: dashboardData.subcontractors.newCount > 0
    },
    {
      key: 'wall',
      title: 'ŞUBE DUVARI',
      icon: StickyNote,
      path: '/work/wall',
      color: 'pink',
      newCount: dashboardData.wall.newCount,
      items: dashboardData.wall.items,
      showIf: dashboardData.wall.newCount > 0 || dashboardData.wall.items.length > 0
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-600' },
      indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-600' },
      green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-600' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-600' },
      amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-600' },
      pink: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', badge: 'bg-pink-600' }
    };
    return colors[color] || colors.blue;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Bugün ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Yarın ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  // Filter cards that have data to show
  const visibleCards = summaryCards.filter(card => card.showIf);

  return (
    <div className="space-y-6" data-testid="work-dashboard">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Hoş geldin, {user?.name}!</h1>
        <p className="text-white/80">
          {visibleCards.length > 0 
            ? `${visibleCards.length} kategoride yeni bildiriminiz var.`
            : 'Tüm işleriniz güncel görünüyor.'
          }
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : visibleCards.length === 0 ? (
        // Hiç yeni içerik yoksa basit bir mesaj göster
        <Card className="border-2 border-dashed border-gray-200">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Her şey güncel!</h3>
            <p className="text-gray-500 mb-4">Şu anda bekleyen bildiriminiz veya yaklaşan etkinliğiniz yok.</p>
            <div className="flex justify-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/work/jobs')}
                className="gap-2"
              >
                <Briefcase className="h-4 w-4" />
                İşlere Git
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/work/calendar')}
                className="gap-2"
              >
                <Calendar className="h-4 w-4" />
                Takvime Git
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Veri olan kartları grid olarak göster
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCards.map((card) => {
            const Icon = card.icon;
            const colors = getColorClasses(card.color);
            
            return (
              <Card 
                key={card.key}
                className={`${colors.bg} ${colors.border} border-2 hover:shadow-lg transition-all cursor-pointer`}
                onClick={() => navigate(card.path)}
                data-testid={`dashboard-card-${card.key}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className={`flex items-center justify-between ${colors.text}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5" />
                      <span className="font-bold">{card.title}</span>
                    </div>
                    {card.newCount > 0 && (
                      <Badge className={`${colors.badge} text-white animate-pulse`}>
                        {card.newCount} yeni
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Özel içerik türüne göre */}
                  {card.key === 'calendar' && (
                    <div className="space-y-1 mb-3">
                      {card.todayCount > 0 && (
                        <p className={`text-sm font-medium ${colors.text}`}>
                          📅 Bugün {card.todayCount} etkinlik
                        </p>
                      )}
                      {card.upcomingCount > 0 && (
                        <p className="text-sm text-gray-600">
                          Bu hafta {card.upcomingCount} etkinlik daha
                        </p>
                      )}
                    </div>
                  )}
                  
                  {card.key === 'assignments' && (
                    <div className="space-y-1 mb-3">
                      {card.newCount > 0 && (
                        <p className={`text-sm font-medium ${colors.text}`}>
                          🔔 {card.newCount} yeni program
                        </p>
                      )}
                      {card.upcomingCount > 0 && (
                        <p className="text-sm text-gray-600">
                          Bu hafta {card.upcomingCount} yaklaşan program
                        </p>
                      )}
                    </div>
                  )}

                  {/* Item listesi */}
                  {card.items && card.items.length > 0 && (
                    <div className="space-y-2">
                      {card.items.map((item, idx) => (
                        <div 
                          key={item.id || idx}
                          className="flex items-center justify-between bg-white/70 rounded-md p-2 text-sm"
                        >
                          <span className="truncate flex-1 text-gray-700">{item.title}</span>
                          {item.date && (
                            <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                              {formatDate(item.date)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Subcontractors özel mesaj */}
                  {card.key === 'subcontractors' && card.newCount > 0 && (
                    <p className="text-sm text-gray-600">
                      {card.newCount} yeni taşeron kaydı
                    </p>
                  )}

                  {/* Git butonu */}
                  <div className="mt-3 pt-2 border-t border-white/50">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className={`w-full ${colors.text} hover:bg-white/50`}
                    >
                      Detaylara Git
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Hızlı Erişim Butonları */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-700 text-lg">Hızlı Erişim</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/work/jobs')} className="gap-2">
              <Briefcase className="h-4 w-4" /> İşler
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/communication')} className="gap-2">
              <Inbox className="h-4 w-4" /> İletişim
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/reports')} className="gap-2">
              <FileText className="h-4 w-4" /> Raporlar
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/assignments')} className="gap-2">
              <Clock className="h-4 w-4" /> İş Programı
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/calendar')} className="gap-2">
              <Calendar className="h-4 w-4" /> Takvim
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/subcontractors')} className="gap-2">
              <HardHat className="h-4 w-4" /> Taşeronlar
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/sube-bilgileri')} className="gap-2">
              <Building2 className="h-4 w-4" /> Şube Bilgileri
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/files')} className="gap-2">
              <FolderOpen className="h-4 w-4" /> Dosyalar
            </Button>
            <Button variant="outline" onClick={() => navigate('/work/wall')} className="gap-2">
              <StickyNote className="h-4 w-4" /> Şube Duvarı
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkDashboard;
